/**
 * LoginPage Component
 *
 * Authentication page using synapse design system components.
 * Supports adding additional accounts via ?mode=addAccount query param.
 */

import { useState, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button, Input, Label, Card, CardContent } from '@lognai/synapse-design-system-v2';
import { useAuth } from '../../hooks/useAuth';
import { accountManager } from '../../utils/accountManager';
import { Logo } from '../../components/Logo';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isLoggingIn, loginError } = useAuth();

  const isAddAccountMode = searchParams.get('mode') === 'addAccount';
  const existingAccountCount = accountManager.getAccountCount();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);

    try {
      const response = await login({ email, password });

      // Check if login was successful
      if (response.success && response.data) {
        if (isAddAccountMode) {
          // In add account mode, go back or to the workspace
          navigate('/workspace');
        } else {
          navigate('/workspace');
        }
      } else {
        // API returned error in response
        setFormError(response.error || 'Login failed');
      }
    } catch (err) {
      // Network or other error
      console.error('Login failed:', err);
    }
  };

  const handleCancel = () => {
    // Go back to previous page
    navigate(-1);
  };

  return (
    <Card className="w-full">
      <div className="space-y-3 px-5 pt-6 pb-5 text-center">
        <div className="flex justify-center">
          {/* Surface is inherited from the theme — the auth card follows it. Sized larger
              than the sidebar so the wordmark's tagline stays legible. */}
          <Logo className="h-20 w-auto" />
        </div>
        <h1 className="text-xl font-normal text-foreground">
          {isAddAccountMode ? 'Add Another Account' : 'Sign in to your account'}
        </h1>
        {isAddAccountMode && (
          <p className="text-sm text-muted-foreground">
            Sign in with a different account. You have {existingAccountCount} account{existingAccountCount !== 1 ? 's' : ''} already signed in.
          </p>
        )}
      </div>

      <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {!isAddAccountMode && (
                  <Link
                    to="/forgot-password"
                    className="text-xs text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
                  >
                    Forgot password?
                  </Link>
                )}
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
              />
            </div>

            {(formError || loginError) && (
              <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                {formError || loginError?.message || 'Login failed'}
              </div>
            )}

            <div className="flex gap-3">
              {isAddAccountMode && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                className={isAddAccountMode ? 'flex-1' : 'w-full'}
                disabled={isLoggingIn}
              >
                {isLoggingIn ? 'Signing in...' : isAddAccountMode ? 'Add Account' : 'Sign In'}
              </Button>
            </div>
          </form>

          {/* Demo Credentials - only show when not in add account mode */}
          {/* {!isAddAccountMode && (
            <div className="mt-6 rounded-lg bg-muted p-4 text-xs text-muted-foreground">
              <div className="font-semibold mb-2">Test Accounts:</div>
              <div className="space-y-1">
                <div>Platform Admin: platform@lognai.com</div>
                <div>Company Admin: admin@acme.com</div>
                <div>Password (all): password</div>
              </div>
            </div>
          )} */}
      </CardContent>
    </Card>
  );
}
